import type { FC, ReactNode } from 'react'
import type { TeamMember } from '@/types'

import Image from 'next/image'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { Mark } from '@/components/generals/mark'
import { PlaceholderTag } from '@/components/generals/placeholder-tag'
import { SectionLabel } from '@/components/generals/section-label'

interface TeamSectionProps {
    team: TeamMember[]
    label?: string
}

/* Varied portrait proportions and offsets so the team reads as an editorial spread, not a card grid. */
const LAYOUT = [
    { cell: 'lg:col-span-3', aspect: 'aspect-[4/5]' },
    { cell: 'lg:col-span-3 lg:mt-24', aspect: 'aspect-[3/4]' },
    { cell: 'lg:col-span-3 lg:mt-8', aspect: 'aspect-square' },
    { cell: 'lg:col-span-3 lg:mt-32', aspect: 'aspect-[4/5]' }
]

const TeamSection: FC<TeamSectionProps> = ({ team, label = 'The team' }): ReactNode => {
    const hasPlaceholders = team.some((member) => member.isPlaceholder)

    return (
        <section id='team' aria-labelledby='team-title' data-nav-theme='dark' className='bg-charcoal text-cream'>
            <div className='mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28'>
                <div className='flex flex-col gap-8 md:flex-row md:items-end md:justify-between'>
                    <div data-reveal>
                        <SectionLabel tone='dark'>{label}</SectionLabel>
                        <h2 id='team-title' className='mt-6 font-heading type-section'>
                            <span className='md:block'>The people</span>{' '}
                            <span className='md:block'>behind <Mark tone='dark'>the ideas.</Mark></span>
                        </h2>
                    </div>
                    <div data-reveal className='max-w-sm space-y-4 md:text-right'>
                        <p className='text-[16px] leading-relaxed text-cream/80'>
                            Strategists, creatives and performance specialists working as one team on every account.
                        </p>
                        {hasPlaceholders && <PlaceholderTag tone='dark'>Editable team placeholders</PlaceholderTag>}
                    </div>
                </div>

                {team.length > 0 ? (
                    <ul className='mt-14 grid gap-x-6 gap-y-14 sm:grid-cols-2 md:mt-20 lg:grid-cols-12'>
                        {team.map((member, index) => {
                            const cell = LAYOUT[index % LAYOUT.length]

                            return (
                                <li key={member.id} data-reveal className={cn(cell.cell)}>
                                    <article className='group'>
                                        <div data-reveal-image className={cn('relative overflow-hidden bg-charcoal-soft', cell.aspect)}>
                                            {member.photo && (
                                                <Image
                                                    src={member.photo}
                                                    alt={member.photoAlt}
                                                    fill
                                                    sizes='(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw'
                                                    className='object-cover object-top saturate-[0.85] transition-[scale,filter] duration-700 ease-[var(--ease-out-expo)] group-hover:scale-[1.03] group-hover:saturate-100'
                                                />
                                            )}
                                            <span className='absolute left-3 top-3 rounded-sm bg-charcoal/80 px-2 py-1 font-mono text-[10px] text-cream'>
                                                / {pad(index + 1)}
                                            </span>
                                        </div>

                                        <div className='mt-5 flex items-start justify-between gap-4'>
                                            <div>
                                                <h3 className='font-heading font-wide text-xl font-black uppercase tracking-[-0.015em]'>
                                                    {member.name}
                                                </h3>
                                                <p className='mt-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-coral'>
                                                    {member.role}
                                                </p>
                                            </div>
                                            {member.linkedinUrl && (
                                                <a
                                                    href={member.linkedinUrl}
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                    aria-label={`${member.name} on LinkedIn`}
                                                    className='flex size-9 shrink-0 items-center justify-center rounded-sm border border-cream/30 transition-colors hover:border-coral hover:bg-coral hover:text-charcoal'
                                                >
                                                    <ArrowUpRight aria-hidden='true' className='size-4' />
                                                </a>
                                            )}
                                        </div>

                                        <p className='mt-3 max-w-sm text-[15px] leading-relaxed text-cream/80'>
                                            {member.bio}
                                        </p>
                                        {member.expertise && (
                                            <p className='mt-4 border-t border-cream/15 pt-3 text-[13px] text-cream/75'>
                                                <span className='font-mono text-[10px] uppercase tracking-[0.18em] text-cream/60'>Expertise </span>
                                                {member.expertise}
                                            </p>
                                        )}
                                    </article>
                                </li>
                            )
                        })}
                    </ul>
                ) : (
                    <p className='mt-16 text-cream/70'>Team profiles will appear here once they are added.</p>
                )}
            </div>
        </section>
    )
}

export default TeamSection
